import React, { useState } from 'react';
import TopBar from '../TopBar';
import Navbar from '../Navbar';
import Footer from '../Footer';
import SEO from '../SEO';
import { sendAdminNotification } from '../notificationService';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [successMessage, setSuccessMessage] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [errors, setErrors] = useState({});

  const faqItems = [
    {
      id: 1,
      question: "What is your typical project timeline?",
      answer: "Timelines depend on scope and complexity. Discovery and planning usually take 1-2 weeks. A standard business website often takes 4-6 weeks, while custom web apps, ERP systems, or mobile products can take 2-6 months. After the discovery call, we share a milestone-based delivery plan."
    },
    {
      id: 2,
      question: "What details should I share in the first consultation?",
      answer: "Share your business goals, current challenges, expected features, preferred timeline, and budget range. If you already have an existing system or vendor setup, we can review that and suggest a practical execution roadmap."
    },
    {
      id: 3,
      question: "Do you provide post-launch support and maintenance?",
      answer: "Yes. We provide ongoing support, monitoring, bug fixes, updates, and enhancement assistance. Our standard support window is business hours (10 AM to 5 PM IST), and critical production issues are prioritized as per agreed response commitments."
    },
    {
      id: 4,
      question: "Can you work with our existing software or vendor setup?",
      answer: "Absolutely. We can audit your current system, integrate with existing tools/APIs, and execute phased migration plans so your operations continue smoothly during transition."
    },
    {
      id: 5,
      question: "How do you handle security and compliance requirements?",
      answer: "We follow a security-first approach with role-based access, secure coding, encrypted communication, and deployment hardening. For regulated industries, we design compliance-aware solutions aligned with applicable project requirements."
    },
    {
      id: 6,
      question: "Do you sign NDA before project discussions?",
      answer: "Yes. We can sign an NDA before detailed technical discussions so you can share product, architecture, and business information confidently."
    },
    {
      id: 7,
      question: "How do pricing and engagement models work?",
      answer: "We offer flexible engagement models: fixed-scope projects, milestone-based delivery, and dedicated team support. Final pricing depends on scope, complexity, integrations, and support expectations."
    },
    {
      id: 8,
      question: "How quickly will your team respond after I submit the form?",
      answer: "Our team typically responds within 24 business hours. For urgent discussions, you can call us directly on the listed contact numbers."
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // First Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.firstName)) {
      newErrors.firstName = 'First name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Last Name validation (optional)
    if (formData.lastName.trim() && !/^[a-zA-Z\s'-]+$/.test(formData.lastName)) {
      newErrors.lastName = 'Last name can only contain letters, spaces, hyphens, and apostrophes';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 100) {
      newErrors.email = 'Email must be less than 100 characters';
    }
    
    // Phone validation (optional but if provided, must be valid)
    if (formData.phone.trim()) {
      // Remove all non-digit characters except leading +
      const phoneDigits = formData.phone.replace(/[^\d+]/g, '');
      
      // Check for +91 (India) and 10 digits
      if (phoneDigits.startsWith('+91')) {
        if (!/^\+91\d{10}$/.test(phoneDigits)) {
          newErrors.phone = 'Phone number must be 10 digits after +91 country code';
        }
      } else {
        // Check for 10 digits and valid Indian mobile (starts with 6-9)
        if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
          newErrors.phone = 'Please enter a valid 10-digit Indian phone number (starting with 6-9)';
        }
      }
    }
    
    // Service validation (optional)
    if (formData.service.trim() && formData.service.length < 3) {
      newErrors.service = 'Service description must be at least 3 characters';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Project details are required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Please provide at least 10 characters describing your project';
    } else if (formData.message.length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Create submission object for server
    const submissionData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      service: formData.service,
      message: formData.message,
      source: "contact_page"
    };
    
    try {
      // Send to server
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Also save to localStorage as backup
        const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
        existingSubmissions.push(result.submission);
        localStorage.setItem('contactSubmissions', JSON.stringify(existingSubmissions));
        
        // Send notification to admin
        sendAdminNotification(result.submission);
        
        // Show success message
        setSuccessMessage(true);
        
        // Auto-scroll to success message
        setTimeout(() => {
          const successElement = document.querySelector('[data-success-message]');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
        setErrors({});
        
        // Hide success message after 5 seconds
        setTimeout(() => setSuccessMessage(false), 5000);
      } else {
        const errorData = await response.json();
        setErrors({ submit: errorData.error || 'Failed to submit form' });
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Fallback to localStorage if server is unavailable
      const newSubmission = {
        id: Date.now(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        submissionDate: new Date().toISOString(),
        source: "contact_page"
      };
      
      const existingSubmissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
      existingSubmissions.push(newSubmission);
      localStorage.setItem('contactSubmissions', JSON.stringify(existingSubmissions));
      
      // Send notification
      sendAdminNotification(newSubmission);
      
      // Show success anyway (saved to localStorage)
      setSuccessMessage(true);
      
      // Auto-scroll to success message
      setTimeout(() => {
        const successElement = document.querySelector('[data-success-message]');
        if (successElement) {
          successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setErrors({});
      
      // Hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(false), 5000);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form-section').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Garud Stacks - Get Enterprise Technology Solutions"
        description="Get in touch with Garud Stacks for enterprise technology solutions, custom software development, AI, cloud computing, and digital transformation services."
        image="https://garudstacks.tech/img/contact-og.png"
        url="https://garudstacks.tech/contact"
        keywords="contact garud stacks, enterprise solutions, software development services, technology consulting"
      />
      <TopBar />
      <Navbar />

      {/* ================= HERO SECTION ================= */}
      <section className="relative h-screen sm:h-[55vh] flex items-center justify-center overflow-hidden group">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: "url('../img/small.png')",
              filter: "brightness(0.6) saturate(1.1)"
            }}
          ></div>
        </div>
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 via-gray-900/70 to-black/50 z-5"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-5"></div>

        {/* Content */}
        <div className="relative z-20 max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12 text-center">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/40 backdrop-blur-md mb-4 sm:mb-6 group-hover:border-amber-500/60 transition-colors duration-300">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            <span className="text-xs sm:text-sm font-bold text-amber-300 uppercase tracking-widest">Let's Connect</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-3 sm:mb-6 text-white uppercase tracking-tight">
            Ready to Transform <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-red-400">Your Business?</span>
          </h1>

          {/* Description */}
          <p className="text-gray-300 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-6 sm:mb-8 font-medium px-1">
            Our expert team is just one call away. Get in touch and let's discuss your digital transformation.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
            <button 
              onClick={scrollToForm}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-bold uppercase tracking-widest text-xs sm:text-sm bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-2xl hover:shadow-amber-500/50 hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 group/btn"
            >
              Send Message
              <svg className="w-4 sm:w-5 h-4 sm:h-5 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            <button 
              onClick={() => window.location.href = 'tel:+919522950225'}
              className="w-full sm:w-auto px-6 sm:px-8 py-3 rounded-lg font-bold uppercase tracking-widest text-xs sm:text-sm border-2 border-white hover:border-amber-400 text-white hover:bg-amber-500/10 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3"
            >
              <svg className="w-4 sm:w-5 h-4 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span className="hidden sm:inline">Call Now</span>
              <span className="sm:hidden">Call</span>
            </button>
          </div>
        </div>
      </section>

      {/* ================= MAIN CONTACT SECTION ================= */}
      <section className="relative bg-gray-50 py-20 lg:py-32 overflow-hidden border-b border-gray-200">
        <style jsx>{`
          .bg-blueprint {
            background-image: 
              linear-gradient(0deg, transparent 24%, rgba(255, 165, 0, 0.05) 25%, rgba(255, 165, 0, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 165, 0, 0.05) 75%, rgba(255, 165, 0, 0.05) 76%, transparent 77%, transparent),
              linear-gradient(90deg, transparent 24%, rgba(255, 165, 0, 0.05) 25%, rgba(255, 165, 0, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 165, 0, 0.05) 75%, rgba(255, 165, 0, 0.05) 76%, transparent 77%, transparent);
            background-size: 50px 50px;
          }
        `}</style>
        <div className="absolute inset-0 bg-blueprint opacity-40 z-0 pointer-events-none"></div>
        
        <div id="contact-form-section" className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
          
          {/* Left Side: Contact Form (Takes 3 columns) */}
          <div className="lg:col-span-3 bg-white/90 backdrop-blur-xl rounded-[2rem] p-8 sm:p-10 shadow-2xl border border-gray-100 relative overflow-hidden group hover:shadow-[0_10px_40px_-10px_rgba(245,158,11,0.1)] transition-shadow duration-500">
            {/* Accent Corner */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-amber-50 rounded-bl-[100px] z-0 transition-colors duration-500 group-hover:bg-amber-100/50"></div>
            
            <div className="relative z-10 mb-8">
              <h2 className="text-3xl font-extrabold text-gray-900 uppercase tracking-tight mb-2">Send a Message</h2>
              <p className="text-gray-500 text-sm font-medium">Our team will get back to you within 24 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
              {/* Name Row */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">First Name <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="enter first name" 
                    className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 placeholder-gray-400 ${
                      errors.firstName 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15' 
                        : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                    }`}
                    required
                  />
                  {errors.firstName && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Last Name</label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="enter last name" 
                    className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 placeholder-gray-400 ${
                      errors.lastName 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15' 
                        : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                    }`}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.lastName}</p>}
                </div>
              </div>

              {/* Contact Row */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Email Address <span className="text-red-500">*</span></label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="youremailaddress@gmail.com" 
                    className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 placeholder-gray-400 ${
                      errors.email 
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15' 
                        : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                    }`}
                    required
                  />
                  {errors.email && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210" 
                    className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 placeholder-gray-400 ${
                      errors.phone
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15'
                        : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                    }`}
                  />
                  {errors.phone && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.phone}</p>}
                </div>
              </div>

              {/* Service Required */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2">Service Required</label>
                <div className="relative">
                  <select 
                    name="service" 
                    value={formData.service}
                    onChange={handleChange}
                    className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 appearance-none cursor-pointer ${
                      errors.service
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15'
                        : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                    }`}
                  >
                    <option value="" disabled selected>Select an area of interest...</option>
                    <option value="cloud">Cloud Infrastructure & DevOps</option>
                    <option value="cybersecurity">Cyber Security Solutions</option>
                    <option value="enterprise-erp">Enterprise Software / ERP</option>
                    <option value="mobile-apps">Mobile App Development</option>
                    <option value="other">Other / General Inquiry</option>
                  </select>
                  <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {errors.service && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.service}</p>}
              </div>

              {/* Message */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-widest">Project Details <span className="text-red-500">*</span></label>
                  <span className="text-xs text-gray-400">{formData.message.length}/1000</span>
                </div>
                <textarea 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  rows="5" 
                  placeholder="Tell us briefly about your goals, timelines, or technical challenges..." 
                  maxLength="1000"
                  className={`w-full px-4 py-3.5 bg-white border rounded-xl shadow-sm hover:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 transition-all text-sm text-gray-800 placeholder-gray-400 resize-none ${
                    errors.message 
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/15' 
                      : 'border-gray-200 focus:border-amber-500 focus:ring-amber-500/15'
                  }`}
                  required
                ></textarea>
                {errors.message && <p className="text-red-500 text-xs font-bold mt-1.5">{errors.message}</p>}
              </div>

              {/* Submit Btn */}
              <div className="pt-2">
                <button 
                  type="submit" 
                  className="w-full flex justify-center items-center gap-3 py-4 px-6 border border-transparent rounded-xl shadow-[0_10px_25px_-5px_rgba(245,158,11,0.5)] text-sm font-bold uppercase tracking-widest text-black bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 hover:-translate-y-1 transition-all duration-300 group/btn"
                >
                  <span>Submit Request</span>
                  <svg className="w-4 h-4 transform group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              </div>
            </form>

            {/* Success Message */}
            {successMessage && (
              <div data-success-message className="relative z-10 text-center bg-green-50 border-2 border-green-300 p-10 rounded-2xl shadow-lg mt-6 animate-pulse">
                <h3 className="text-3xl font-extrabold text-green-800">Thank You!</h3>
                <p className="text-green-700 mt-3 text-lg">Your message has been sent successfully.</p>
                <p className="text-green-600 mt-1 text-sm">Our team will get back to you shortly.</p>
              </div>
            )}
          </div>

          {/* Right Side: Contact Details (Takes 2 columns) */}
          <div className="lg:col-span-2 flex flex-col h-full gap-8">
            
            {/* Primary Contact Box (Dark) */}
            <div className="bg-gradient-to-br from-gray-900 via-gray-950 to-black border border-gray-800 rounded-[2rem] p-8 text-white shadow-2xl relative overflow-hidden flex-grow flex flex-col justify-between">
              <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl pointer-events-none"></div>
              
              <div className="relative z-10">
                <h3 className="text-xl font-extrabold mb-8 uppercase tracking-tight border-b border-gray-800 pb-4">Direct <span className="text-amber-500">Lines</span></h3>
              
                <div className="space-y-8">
                  {/* Call */}
                  <div className="flex items-start gap-5 group">
                    <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all duration-300 shadow-inner flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Call Us 24/7</p>
                      <a href="tel:+919522950225" className="text-base font-bold text-gray-100 hover:text-amber-500 transition-colors block mb-1">+91 95229 50225</a>
                      <a href="tel:+919806520216" className="text-base font-bold text-gray-100 hover:text-amber-500 transition-colors block">+91 98065 20216</a>
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="flex items-start gap-5 group">
                    <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all duration-300 shadow-inner flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Email Support</p>
                      <a href="mailto:contact@garudstacks.tech" className="text-sm font-bold text-gray-100 hover:text-amber-500 transition-colors block break-all">contact@garudstacks.tech</a>
                      <a href="mailto:garudstacks@gmail.com" className="text-sm font-bold text-gray-100 hover:text-amber-500 transition-colors block break-all">garudstacks@gmail.com</a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-5 group">
                    <div className="w-12 h-12 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center text-amber-500 group-hover:bg-amber-500 group-hover:text-black transition-all duration-300 shadow-inner flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Headquarters</p>
                      <p className="text-sm font-medium text-gray-300 leading-relaxed">
                        Panchwati Colony Phase 3, Karond,<br />Bhopal, Madhya Pradesh 462038
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Support Information Box */}
            <div className="rounded-[2rem] bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 p-8 shadow-inner relative overflow-hidden flex flex-col justify-center">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/50 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none"></div>
              
              <div className="relative z-10">
                <h4 className="text-xl font-extrabold text-gray-900 mb-3 tracking-tight">Dedicated to Your <span className="text-amber-600">Success</span></h4>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Our technical experts are available around the clock to ensure your enterprise systems run flawlessly. Whether you need immediate troubleshooting or strategic consultation, we are here to support your growth.
                </p>
                
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-amber-200/50">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></span>
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-widest">Support Team Online</span>
                  </div>
                  <span className="text-xs font-bold text-amber-600 uppercase tracking-widest">&lt; 24hr Response</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PARTNERSHIP PROCESS SECTION ================= */}
      <section className="relative bg-white py-24 lg:py-32 overflow-hidden border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase text-gray-900 mb-6">Our <span className="text-amber-600">Partnership</span> Journey</h2>
            <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto">From the first handshake to long-term operations, here is how we bring your vision to life.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-gray-200 -z-0 transform -translate-y-12"></div>

            {/* Step 1 */}
            <div className="relative z-10 text-center group">
              <div className="w-20 h-20 bg-gray-900 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:bg-amber-500 group-hover:text-black transition-all duration-500 transform group-hover:-translate-y-2">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h4 className="text-lg font-extrabold uppercase text-gray-900 mb-3 tracking-wide">Discovery</h4>
              <p className="text-sm text-gray-500 leading-relaxed px-4">Deep dive into your technical challenges and business objectives.</p>
            </div>

            {/* Step 2 */}
            <div className="relative z-10 text-center group">
              <div className="w-20 h-20 bg-gray-900 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:bg-amber-500 group-hover:text-black transition-all duration-500 transform group-hover:-translate-y-2">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h4 className="text-lg font-extrabold uppercase text-gray-900 mb-3 tracking-wide">Architecture</h4>
              <p className="text-sm text-gray-500 leading-relaxed px-4">Drafting high-performance blueprints for your custom systems.</p>
            </div>

            {/* Step 3 */}
            <div className="relative z-10 text-center group">
              <div className="w-20 h-20 bg-gray-900 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:bg-amber-500 group-hover:text-black transition-all duration-500 transform group-hover:-translate-y-2">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h4 className="text-lg font-extrabold uppercase text-gray-900 mb-3 tracking-wide">Execution</h4>
              <p className="text-sm text-gray-500 leading-relaxed px-4">Rapid development with strict security and quality compliance.</p>
            </div>

            {/* Step 4 */}
            <div className="relative z-10 text-center group">
              <div className="w-20 h-20 bg-gray-900 text-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:bg-amber-500 group-hover:text-black transition-all duration-500 transform group-hover:-translate-y-2">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-lg font-extrabold uppercase text-gray-900 mb-3 tracking-wide">24/7 Operations</h4>
              <p className="text-sm text-gray-500 leading-relaxed px-4">Ongoing maintenance, scaling, and enterprise-grade support.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SUPPORT EXCELLENCE SECTION ================= */}
      <section className="relative bg-gray-950 py-24 lg:py-32 overflow-hidden border-b border-gray-800">
        <style jsx>{`
          .bg-blueprint {
            background-image: 
              linear-gradient(0deg, transparent 24%, rgba(255, 165, 0, 0.05) 25%, rgba(255, 165, 0, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 165, 0, 0.05) 75%, rgba(255, 165, 0, 0.05) 76%, transparent 77%, transparent),
              linear-gradient(90deg, transparent 24%, rgba(255, 165, 0, 0.05) 25%, rgba(255, 165, 0, 0.05) 26%, transparent 27%, transparent 74%, rgba(255, 165, 0, 0.05) 75%, rgba(255, 165, 0, 0.05) 76%, transparent 77%, transparent);
            background-size: 50px 50px;
          }
        `}</style>
        <div className="absolute inset-0 bg-blueprint opacity-5"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase leading-tight text-white mb-8">
                World-Class <br />
                <span className="text-amber-500">Service Standards</span>
              </h2>
              
              <div className="grid gap-6">
                {/* SLAs */}
                <div className="flex items-center gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl group hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/20 text-amber-500 flex items-center justify-center text-2xl font-bold">01</div>
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wider mb-1">Guaranteed Response</h4>
                    <p className="text-gray-400 text-sm">Under 24-hour response time for critical production issues.</p>
                  </div>
                </div>
                {/* Security */}
                <div className="flex items-center gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl group hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/20 text-amber-500 flex items-center justify-center text-2xl font-bold">02</div>
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wider mb-1">Zero-Trust Protocol</h4>
                    <p className="text-gray-400 text-sm">All communications and data transfers are encrypted and multi-factor verified.</p>
                  </div>
                </div>
                {/* Global */}
                <div className="flex items-center gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl group hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 rounded-lg bg-amber-500/20 text-amber-500 flex items-center justify-center text-2xl font-bold">03</div>
                  <div>
                    <h4 className="text-white font-bold uppercase tracking-wider mb-1">Scale-on-Demand</h4>
                    <p className="text-gray-400 text-sm">Infrastructure scales automatically with your business growth.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visual Image */}
            <div className="relative group">
              <style jsx>{`
                @keyframes float-y {
                  0%, 100% { transform: translateY(0px); }
                  50% { transform: translateY(-20px); }
                }
                .animate-float-y {
                  animation: float-y 6s ease-in-out infinite;
                }
              `}</style>
              <div className="absolute -inset-2 bg-gradient-to-r from-amber-500 to-orange-600 rounded-3xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border border-gray-800 animate-float-y">
                <img 
                  src="../img/call.png" 
                  alt="Tech Support Hub" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FAQ SECTION ================= */}
      <section className="relative bg-white py-20 lg:py-32 overflow-hidden border-b border-gray-200">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[150px] pointer-events-none"></div>
        
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold uppercase text-gray-900 mb-4">Frequently Asked <span className="text-amber-500">Questions</span></h2>
            <p className="text-gray-600 text-base">Find quick answers to common queries about our process and services.</p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item) => (
              <div 
                key={item.id}
                className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden hover:border-amber-300 transition-all duration-300 shadow-sm hover:shadow-lg"
              >
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === item.id ? null : item.id)}
                  className="w-full flex justify-between items-center p-6 hover:bg-gray-100 transition-colors text-left group"
                >
                  <span className="text-gray-900 group-hover:text-amber-600 font-bold text-lg transition-colors">
                    {item.question}
                  </span>
                  <svg 
                    className={`w-5 h-5 text-gray-600 group-hover:text-amber-600 transition-all duration-300 ${
                      expandedFAQ === item.id ? 'rotate-180' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {expandedFAQ === item.id && (
                  <div className="px-6 pb-6 border-t border-gray-200 bg-white">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
